import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Download, Loader2, RefreshCw, Search, ArrowUp, ArrowDown, Info } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useAuth } from '../../context/AuthContext';

/**
 * Daily Traffic Sources (BQ) — /analytics/bq-dashboard "Traffic Sources" sekmesiyle
 * aynı kaynak (bq-analytics/traffic-sources-daily → bq_ga4_traffic_source_daily,
 * High5'ta *_aem görünümü). Tüm kaynaklar, gün gün, metrik kullanıcı seçimi.
 *
 * Oran metrikleri (CR, AOV, RPS, yeni kullanıcı %) hücre bazında toplamlardan
 * hesaplanır; ortalaması alınmaz. Users günlük toplanır → tekil kullanıcı değildir.
 */

const API = 'https://api.glowytics.com/api/bq-analytics';
const COLORS = ['#2563eb', '#f97316', '#16a34a', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d', '#ca8a04', '#475569'];

const fmtInt = (n) => Math.round(Number(n) || 0).toLocaleString('tr-TR');
const fmtCur = (n) => '₺' + Math.round(Number(n) || 0).toLocaleString('tr-TR');
const fmtPct = (n) => (Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
const fmtCur2 = (n) => '₺' + (Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const div = (a, b) => (b > 0 ? a / b : 0);

const METRICS = [
  { key: 'sessions', label: 'Sessions', calc: (b) => b.sessions, fmt: fmtInt },
  { key: 'users', label: 'Users', calc: (b) => b.users, fmt: fmtInt },
  { key: 'new_users', label: 'New Users', calc: (b) => b.new_users, fmt: fmtInt },
  { key: 'transactions', label: 'Transactions', calc: (b) => b.transactions, fmt: fmtInt },
  { key: 'revenue', label: 'Revenue', calc: (b) => b.revenue, fmt: fmtCur },
  { key: 'cr', label: 'Conv. Rate', calc: (b) => div(b.transactions, b.sessions) * 100, fmt: fmtPct, ratio: true },
  { key: 'aov', label: 'AOV', calc: (b) => div(b.revenue, b.transactions), fmt: fmtCur, ratio: true },
  { key: 'rps', label: 'Revenue / Session', calc: (b) => div(b.revenue, b.sessions), fmt: fmtCur2, ratio: true },
  { key: 'new_pct', label: 'New User %', calc: (b) => div(b.new_users, b.users) * 100, fmt: fmtPct, ratio: true },
  { key: 'share_sessions', label: 'Session Share %', share: 'sessions', fmt: fmtPct, ratio: true },
  { key: 'share_revenue', label: 'Revenue Share %', share: 'revenue', fmt: fmtPct, ratio: true },
];
const METRIC_BY_KEY = Object.fromEntries(METRICS.map((m) => [m.key, m]));

const GROUPINGS = [
  { key: 'sm', label: 'Source / Medium' },
  { key: 'source', label: 'Source' },
  { key: 'medium', label: 'Medium' },
  { key: 'channel', label: 'Channel' },
];

const PRESETS = [
  { key: 'last7', label: '7 Days' },
  { key: 'last14', label: '14 Days' },
  { key: 'last30', label: '30 Days' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'custom', label: 'Custom' },
];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function presetRange(key) {
  const today = new Date();
  const yest = new Date(today); yest.setDate(yest.getDate() - 1);
  const back = (n) => { const s = new Date(yest); s.setDate(s.getDate() - (n - 1)); return { startDate: ymd(s), endDate: ymd(yest) }; };
  switch (key) {
    case 'last7': return back(7);
    case 'last14': return back(14);
    case 'last30': return back(30);
    case 'thisMonth': return { startDate: ymd(new Date(today.getFullYear(), today.getMonth(), 1)), endDate: ymd(yest) };
    case 'lastMonth': return {
      startDate: ymd(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      endDate: ymd(new Date(today.getFullYear(), today.getMonth(), 0)),
    };
    default: return back(14);
  }
}

function daysBetween(start, end) {
  const out = [];
  const d = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  while (d <= e && out.length < 400) { out.push(ymd(d)); d.setDate(d.getDate() + 1); }
  return out;
}

const dayLabel = (d) => {
  const dt = new Date(d + 'T00:00:00');
  return `${dt.getDate()} ${dt.toLocaleString('tr-TR', { month: 'short' })}`;
};
const weekday = (d) => new Date(d + 'T00:00:00').toLocaleString('tr-TR', { weekday: 'short' });

/* bq-analytics.js daily-sources içindeki toChannel ile birebir aynı eşleme */
function toChannel(source, medium) {
  const s = (source || '').toLowerCase();
  const m = (medium || '').toLowerCase();
  if ((s.includes('google') || s === 'googleads') && (m === 'cpc' || m === 'paid' || m === 'pmax' || m === 'shopping')) return 'Google Ads';
  if ((s.includes('facebook') || s.includes('fb') || s.includes('meta') || s.includes('instagram') || s.includes('ig')) && m !== 'organic' && m !== 'referral' && m !== 'social') return 'Meta Ads';
  if (s.includes('criteo')) return 'Criteo';
  if (s.includes('rtb')) return 'RTB House';
  if (s.includes('pinterest') && m !== 'social') return 'Pinterest';
  if (m === 'organic') return 'Organic Search';
  if ((s === 'google' || s === 'bing' || s === 'yahoo' || s === 'yandex') && m === '(not set)') return 'Organic Search';
  if (m === 'referral') return 'Referral';
  if (m === 'email' || s.includes('mail') || s.includes('emarsys') || s.includes('klaviyo') || s.includes('brevo')) return 'Email';
  if (s === '(direct)' || (s === '(not set)' && m === '(not set)') || m === '(none)') return 'Direct';
  if (m === 'social' || s.includes('twitter') || s.includes('tiktok') || s.includes('linkedin') || s.includes('youtube')) return 'Social';
  if (m === 'affiliate') return 'Affiliate';
  if (m === 'display' || m === 'banner') return 'Display';
  if (m === 'sms' || s.includes('sms')) return 'SMS';
  if (m === 'push' || s.includes('push') || s.includes('notification')) return 'Push';
  return 'Other';
}

function groupKey(r, grouping) {
  const src = r.source || '(direct)';
  const med = r.medium || '(none)';
  if (grouping === 'source') return src;
  if (grouping === 'medium') return med;
  if (grouping === 'channel') return toChannel(src, med);
  return `${src} / ${med}`;
}

const emptyBucket = () => ({ sessions: 0, users: 0, new_users: 0, transactions: 0, revenue: 0 });
const addInto = (b, r) => {
  b.sessions += Number(r.sessions || 0);
  b.users += Number(r.users || 0);
  b.new_users += Number(r.new_users || 0);
  b.transactions += Number(r.transactions || 0);
  b.revenue += Number(r.revenue || 0);
};

export default function BQTrafficSourcesDaily() {
  const { selectedBrand } = useAuth();
  const [preset, setPreset] = useState('last14');
  const [range, setRange] = useState(presetRange('last14'));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [metricKey, setMetricKey] = useState('sessions');
  const [grouping, setGrouping] = useState('sm');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ col: 'total', dir: 'desc' });
  const [heatmap, setHeatmap] = useState(true);
  const [hideEmpty, setHideEmpty] = useState(true);
  const [chartKeys, setChartKeys] = useState(null); // null = otomatik ilk 5


  const load = () => {
    if (!selectedBrand?.id) return;
    setLoading(true); setError(null);
    fetch(`${API}/traffic-sources-daily?startDate=${range.startDate}&endDate=${range.endDate}`, {
      credentials: 'include',
      headers: { 'X-Brand-ID': selectedBrand.id },
    })
      .then((r) => { if (!r.ok) throw new Error(`API error ${r.status}`); return r.json(); })
      .then((d) => setRows(d?.rows || []))
      .catch((e) => { setError(e.message); setRows([]); })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [selectedBrand?.id, range.startDate, range.endDate]);

  // Henüz senkronlanmamış son günleri (dün çoğu zaman) sütun olarak gösterme
  const dates = useMemo(() => {
    const all = daysBetween(range.startDate, range.endDate);
    const last = rows.reduce((m, r) => (r.date > m ? r.date : m), '');
    return last ? all.filter((d) => d <= last) : all;
  }, [range, rows]);

  // grup → { days: {date: bucket}, total: bucket }
  const pivot = useMemo(() => {
    const groups = {};
    const dayTotals = {};
    const grand = emptyBucket();
    for (const r of rows) {
      const k = groupKey(r, grouping);
      if (!groups[k]) groups[k] = { key: k, days: {}, total: emptyBucket() };
      const g = groups[k];
      if (!g.days[r.date]) g.days[r.date] = emptyBucket();
      addInto(g.days[r.date], r);
      addInto(g.total, r);
      if (!dayTotals[r.date]) dayTotals[r.date] = emptyBucket();
      addInto(dayTotals[r.date], r);
      addInto(grand, r);
    }
    return { groups: Object.values(groups), dayTotals, grand };
  }, [rows, grouping]);

  // AEM görünümü (High5) users/new_users taşımıyor → o metrikleri gizle
  const hasUsers = pivot.grand.users > 0;
  const metrics = hasUsers ? METRICS : METRICS.filter((m) => !['users', 'new_users', 'new_pct'].includes(m.key));
  const metric = metrics.find((m) => m.key === metricKey) || METRIC_BY_KEY.sessions;

  const val = (bucket, denom) => {
    if (!bucket) return 0;
    if (metric.share) return div(bucket[metric.share], denom?.[metric.share] || 0) * 100;
    return metric.calc(bucket);
  };

  const tableRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = pivot.groups
      .filter((g) => !q || g.key.toLowerCase().includes(q))
      .map((g) => {
        const cells = {};
        for (const d of dates) cells[d] = val(g.days[d], pivot.dayTotals[d]);
        return { key: g.key, cells, total: val(g.total, pivot.grand), base: g.total };
      });
    if (hideEmpty) list = list.filter((r) => r.base.sessions > 0 || r.base.transactions > 0 || r.base.revenue > 0);
    const dir = sort.dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (sort.col === 'name') return dir * a.key.localeCompare(b.key, 'tr');
      const av = sort.col === 'total' ? a.total : a.cells[sort.col] || 0;
      const bv = sort.col === 'total' ? b.total : b.cells[sort.col] || 0;
      return dir * (av - bv);
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pivot, dates, metricKey, search, sort, hideEmpty]);

  const footer = useMemo(() => {
    const cells = {};
    for (const d of dates) cells[d] = val(pivot.dayTotals[d], pivot.dayTotals[d]);
    return { cells, total: val(pivot.grand, pivot.grand) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pivot, dates, metricKey]);

  // ısı haritası: satır içi değil, tablo genelinde (toplam sütunu hariç) maksimuma göre
  const cellMax = useMemo(() => {
    let m = 0;
    for (const r of tableRows) for (const d of dates) m = Math.max(m, r.cells[d] || 0);
    return m;
  }, [tableRows, dates]);

  // Grafikte gösterilecek seriler — kullanıcı seçmediyse oturumca ilk 5
  // (oran metriklerinde bile: düşük hacimli kaynakların %'si grafiği boğmasın)
  const effectiveChartKeys = useMemo(() => {
    if (chartKeys) return chartKeys.filter((k) => pivot.groups.some((g) => g.key === k));
    return [...pivot.groups]
      .sort((a, b) => b.total.sessions - a.total.sessions)
      .slice(0, 5)
      .map((g) => g.key);
  }, [chartKeys, pivot]);

  useEffect(() => { setChartKeys(null); }, [grouping]);

  const chartData = useMemo(() => {
    const byKey = Object.fromEntries(pivot.groups.map((g) => [g.key, g]));
    return dates.map((d) => {
      const pt = { date: dayLabel(d) };
      for (const k of effectiveChartKeys) pt[k] = Number(val(byKey[k]?.days[d], pivot.dayTotals[d]).toFixed(2));
      return pt;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pivot, dates, effectiveChartKeys, metricKey]);

  const toggleChart = (k) => {
    const cur = effectiveChartKeys;
    if (cur.includes(k)) setChartKeys(cur.filter((x) => x !== k));
    else if (cur.length < 10) setChartKeys([...cur, k]);
  };

  const setSortCol = (col) => setSort((s) => (s.col === col ? { col, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { col, dir: col === 'name' ? 'asc' : 'desc' }));

  const applyPreset = (k) => { setPreset(k); if (k !== 'custom') setRange(presetRange(k)); };

  const exportExcel = () => {
    const groupLabel = GROUPINGS.find((g) => g.key === grouping)?.label;
    const round = (v) => Number((Number(v) || 0).toFixed(2));
    const head = [groupLabel, `Total ${metric.label}`, ...dates];
    const pivotSheet = [
      head,
      ...tableRows.map((r) => [r.key, round(r.total), ...dates.map((d) => round(r.cells[d]))]),
      ['TOTAL', round(footer.total), ...dates.map((d) => round(footer.cells[d]))],
    ];
    const raw = [['Date', 'Source', 'Medium', 'Channel', 'Sessions', 'Users', 'New Users', 'Transactions', 'Revenue']];
    for (const r of rows) {
      raw.push([r.date, r.source, r.medium, toChannel(r.source, r.medium), Number(r.sessions || 0), Number(r.users || 0), Number(r.new_users || 0), Number(r.transactions || 0), round(r.revenue)]);
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(pivotSheet), metric.label.slice(0, 31));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(raw), 'Raw daily');
    const slug = (selectedBrand?.name || 'brand').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    XLSX.writeFile(wb, `traffic-sources-daily_${slug}_${metric.key}_${range.startDate}_${range.endDate}.xlsx`);
  };

  const SortIcon = ({ col }) => (sort.col !== col ? null : sort.dir === 'desc' ? <ArrowDown size={10} className="inline ml-0.5" /> : <ArrowUp size={10} className="inline ml-0.5" />);

  const g = pivot.grand;

  return (
    <div className="p-6 space-y-5 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Daily Traffic Sources</h1>
          <p className="text-sm text-gray-500 mt-0.5">BigQuery GA4 · all sources, day by day · {selectedBrand?.name || '—'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex bg-white border border-gray-200 rounded-lg p-0.5">
            {PRESETS.map((p) => (
              <button key={p.key} onClick={() => applyPreset(p.key)}
                className={`px-3 py-1.5 text-xs rounded-md ${preset === p.key ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                {p.label}
              </button>
            ))}
          </div>
          <input type="date" value={range.startDate} max={range.endDate}
            onChange={(e) => { setPreset('custom'); setRange((r) => ({ ...r, startDate: e.target.value })); }}
            className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white" />
          <span className="text-gray-400 text-xs">→</span>
          <input type="date" value={range.endDate} min={range.startDate}
            onChange={(e) => { setPreset('custom'); setRange((r) => ({ ...r, endDate: e.target.value })); }}
            className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white" />
          <button onClick={load} className="p-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100" title="Refresh">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* KPI şeridi */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          ['Sessions', fmtInt(g.sessions)],
          hasUsers ? ['Users (daily sum)', fmtInt(g.users)] : ['AOV', fmtCur(div(g.revenue, g.transactions))],
          ['Transactions', fmtInt(g.transactions)],
          ['Revenue', fmtCur(g.revenue)],
          ['Conv. Rate', fmtPct(div(g.transactions, g.sessions) * 100)],
          ['Sources', fmtInt(pivot.groups.length)],
        ].map(([l, v]) => (
          <div key={l} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className="text-[11px] text-gray-500">{l}</div>
            <div className="text-lg font-semibold text-gray-900 mt-0.5">{v}</div>
          </div>
        ))}
      </div>

      {/* Kontroller */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 w-16">Metric</span>
          {metrics.map((m) => (
            <button key={m.key} onClick={() => setMetricKey(m.key)}
              className={`px-3 py-1 text-xs rounded-full border ${metric.key === m.key ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-blue-300'}`}>
              {m.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 w-16">Group by</span>
          {GROUPINGS.map((gr) => (
            <button key={gr.key} onClick={() => setGrouping(gr.key)}
              className={`px-3 py-1 text-xs rounded-full border ${grouping === gr.key ? 'bg-gray-900 border-gray-900 text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-400'}`}>
              {gr.label}
            </button>
          ))}
          <div className="relative ml-auto">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter sources…"
              className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg w-56" />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input type="checkbox" checked={heatmap} onChange={(e) => setHeatmap(e.target.checked)} /> Heatmap
          </label>
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} /> Hide empty
          </label>
          <button onClick={exportExcel} disabled={!rows.length}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40">
            <Download size={13} /> Excel
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{error}</div>}

      {loading && !rows.length ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-blue-600" /></div>
      ) : !rows.length ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center text-sm text-gray-500">No BigQuery traffic data for this brand and date range.</div>
      ) : (
        <>
          {/* Trend */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800">{metric.label} by day</h3>
              <span className="text-[11px] text-gray-400">Tick rows in the table to change lines (max 10)</span>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} width={70} tickFormatter={(v) => metric.fmt(v)} />
                <Tooltip formatter={(v) => metric.fmt(v)} contentStyle={{ fontSize: 12, borderRadius: 10, border: '1px solid #e5e7eb' }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {effectiveChartKeys.map((k, i) => (
                  <Line key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Pivot */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800">
                {GROUPINGS.find((x) => x.key === grouping)?.label} × day · {metric.label}
              </h3>
              <span className="text-[11px] text-gray-400">{tableRows.length} rows · click headers to sort</span>
            </div>
            <div className="overflow-auto max-h-[70vh]">
              <table className="text-xs border-separate border-spacing-0 min-w-full">
                <thead>
                  <tr>
                    <th className="sticky top-0 left-0 z-30 bg-gray-50 border-b border-r border-gray-200 px-3 py-2 text-left font-medium text-gray-600 min-w-[240px] cursor-pointer"
                      onClick={() => setSortCol('name')}>
                      {GROUPINGS.find((x) => x.key === grouping)?.label}<SortIcon col="name" />
                    </th>
                    <th className="sticky top-0 z-20 bg-blue-50 border-b border-r border-gray-200 px-3 py-2 text-right font-semibold text-blue-700 cursor-pointer whitespace-nowrap"
                      onClick={() => setSortCol('total')}>
                      Total<SortIcon col="total" />
                    </th>
                    {dates.map((d) => {
                      const wd = new Date(d + 'T00:00:00').getDay();
                      return (
                        <th key={d} onClick={() => setSortCol(d)}
                          className={`sticky top-0 z-20 border-b border-gray-200 px-2 py-1.5 text-right font-medium cursor-pointer whitespace-nowrap ${wd === 0 || wd === 6 ? 'bg-amber-50 text-amber-800' : 'bg-gray-50 text-gray-600'}`}>
                          <div>{dayLabel(d)}<SortIcon col={d} /></div>
                          <div className="text-[10px] font-normal opacity-70">{weekday(d)}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((r) => {
                    const ci = effectiveChartKeys.indexOf(r.key);
                    return (
                      <tr key={r.key} className="hover:bg-gray-50">
                        <td className="sticky left-0 z-10 bg-white border-b border-r border-gray-100 px-3 py-1.5 text-gray-800">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={ci >= 0} onChange={() => toggleChart(r.key)} />
                            {ci >= 0 && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: COLORS[ci % COLORS.length] }} />}
                            <span className="break-all">{r.key}</span>
                          </label>
                        </td>
                        <td className="border-b border-r border-gray-100 px-3 py-1.5 text-right font-semibold text-gray-900 bg-blue-50/40 whitespace-nowrap">
                          {metric.fmt(r.total)}
                        </td>
                        {dates.map((d) => {
                          const v = r.cells[d] || 0;
                          const a = heatmap && cellMax > 0 ? Math.min(1, v / cellMax) : 0;
                          return (
                            <td key={d} className="border-b border-gray-100 px-2 py-1.5 text-right whitespace-nowrap tabular-nums"
                              style={a > 0 ? { background: `rgba(37,99,235,${(0.06 + a * 0.5).toFixed(3)})`, color: a > 0.6 ? '#fff' : undefined } : undefined}>
                              {v ? metric.fmt(v) : <span className="text-gray-300">–</span>}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="sticky left-0 bottom-0 z-30 bg-gray-100 border-t-2 border-r border-gray-300 px-3 py-2 font-semibold text-gray-900">TOTAL</td>
                    <td className="sticky bottom-0 z-20 bg-blue-100 border-t-2 border-r border-gray-300 px-3 py-2 text-right font-semibold text-blue-800 whitespace-nowrap">{metric.fmt(footer.total)}</td>
                    {dates.map((d) => (
                      <td key={d} className="sticky bottom-0 z-20 bg-gray-100 border-t-2 border-gray-300 px-2 py-2 text-right font-semibold text-gray-900 whitespace-nowrap tabular-nums">
                        {metric.fmt(footer.cells[d])}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <p className="flex items-start gap-1.5 text-[11px] text-gray-400">
            <Info size={12} className="mt-0.5 flex-shrink-0" />
            Same source as BigQuery Analytics → Traffic Sources. Ratio metrics are computed from summed values per cell. {hasUsers ? 'Users are summed per day, so multi-day totals are not unique users. ' : 'User metrics are not available for this brand\'s source table. '}Revenue without a session source (shown as "Unattributed" in the Daily table) is not included here.
          </p>
        </>
      )}
    </div>
  );
}
